// FIXME: We are not importing via `~utils` due to a circular dependency
import { DateTime } from "luxon";
import { translate } from "utils/string-table";

import { IssueModel, IssueResponse } from "./IssueModel";
import { DEFAULT_SCHOOL_END_TIME, DEFAULT_SCHOOL_START_TIME } from "./SchoolHoursModel";

/***** Response Type ****/
export type IntervalResponse = {
  "db/id": number;
  "interval/start": string;
  "interval/start-offset": number;
  "interval/end": string;
  "interval/end-offset": number;
  "interval/issues"?: Array<IssueResponse>;
};

/***** Model Type *****/
type Interval = {
  // TODO: This should be `id` and be a UUID
  "db/id": number;
  start: DateTime;
  end: DateTime;
  issues?: Array<IssueResponse>;
};

/**
 * Model | IntervalModel
 */
export class IntervalModel {
  static getIntervalById(intervals: IntervalModel[], id: number) {
    return intervals.find((s) => s.eid === id);
  }
  // These are optional to avoid TS type error within useRequestDate.test.tsx tests
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private interval?: any;
  private _issues?: IssueModel[];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(interval: IntervalResponse) {
    this.interval = interval;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this._issues = interval?.["interval/issues"]?.map((issue: any) => {
      return new IssueModel(issue) || [];
    });
  }

  get eid() {
    return this.interval?.["db/id"];
  }

  get localStart() {
    return this.interval?.["interval/start"];
  }

  get startOffset() {
    return this.interval?.["interval/start-offset"];
  }

  // FIXME: We seem to be ignoring the `offset` fields, are we using these?
  get start() {
    // Get start time in whichever timezone the time is in.
    const localStart = this.interval?.["interval/start"];

    // Return a new DateTime object in the same timezone
    const start = DateTime.fromISO(localStart);

    return start;
  }

  get localEnd() {
    return this.interval?.["interval/end"];
  }

  get endOffset() {
    return this.interval?.["interval/end-offset"];
  }

  // FIXME: We seem to be ignoring the `offset` fields, are we using these?
  get end() {
    // Get end time in whichever timezone the time is in.
    const localEnd = this.interval?.["interval/end"];

    // Return a new DateTime object in the same timezone
    const end = DateTime.fromISO(localEnd);

    return end;
  }

  get comments() {
    return this.interval?.["interval/comments"];
  }

  get issues(): IssueModel[] {
    return this._issues || [];
  }

  static getIssueDisplayList(issueType: string): string {
    return INTERVAL_ISSUES[issueType];
  }

  static hasIssues(intervals: IntervalModel[]): boolean {
    for (let i = 0; i < intervals.length; i++) {
      if (intervals[i].issues.length > 0) {
        return true;
      }
    }
    return false;
  }
}

const INTERVAL_ISSUES: Record<string, string> = {
  ISSUE_NO_SHOW_UNCONFIRMED: translate("LABEL_NO_SHOW_UNCONFIRMED"),
  ISSUE_NO_SHOW_CONFIRMED: translate("LABEL_NO_SHOW_CONFIRMED"),
  ISSUE_NO_SHOW_APPROVED: translate("LABEL_NO_SHOW"),
  ISSUE_LUNCH_LATE: translate("REPORT_ISSUE_FORM_LATE_LUNCH"),
  ISSUE_LATE: translate("REPORT_ISSUE_FORM_LATE"),
  ISSUE_SUB_LEFT_EARLY: translate("REPORT_ISSUE_FORM_LEFT_EARLY"),
  ISSUE_SUB_LEFT_EARLY_APPROVED: translate("LABEL_LEFT_EARLY"),
  ISSUE_URGENT_ON_CAMPUS: translate("REPORT_ISSUE_FORM_URGENT_CONCERN"),
};

/***** Factory *****/
/**
 * Creates a IntervalModel given partial Interval information.
 */
export function newInterval(params: Partial<Interval> = {}) {
  const id = params["db/id"] ?? 1;

  let start: string = params.start?.toISO() ?? "";
  let end: string = params.end?.toISO() ?? "";

  // If `start` and `end are not provided, create a new interval for today
  // starting at 7:30am to 3pm
  if (!params.start && !params.end) {
    start = DateTime.fromObject(DEFAULT_SCHOOL_START_TIME, { zone: "utc" }).toISO();
    end = DateTime.fromObject(DEFAULT_SCHOOL_END_TIME, { zone: "utc" }).toISO();
  }
  // If the `start` is provided, but the `end is not
  else if (params.start && !params.end) {
    start = params.start.toUTC().toISO();
    // Then set the end to the same day at the end of the school day
    end = params.start.toUTC().set(DEFAULT_SCHOOL_END_TIME).toISO();
  }
  // If the `end` is provided, but the `start` is not
  else if (params.end && !params.start) {
    start = params.end.toUTC().set(DEFAULT_SCHOOL_START_TIME).toISO();
    // Then set the end to the same day at the end of the school day
    end = params.end.toUTC().toISO();
  }

  return new IntervalModel({
    "db/id": id,
    "interval/start": start,
    "interval/start-offset": 0,
    "interval/end": end,
    "interval/end-offset": 0,
    "interval/issues": params.issues ?? [],
  });
}
